import { createStore, type Store } from 'vuex';
import { NotificationTimer } from './vue/notifications/notification_actions';
import { sendGoogleEvent } from './helper';
import { instance } from './api/instance';
import { DraftsApi } from './api/drafts';

const notificationsStore: Store<any> = createStore({
  state() {
    return {
      notificationTimer: null,
      showNotification: false,
    };
  },
  mutations: {
    SET_NOTIFICATION_TIMER(state: any, payload: any) {
      const tempTimer = state.notificationTimer;
      const delay = 4000;

      if (payload) {
        const { action, timeout, type } = payload;

        if (tempTimer) {
          tempTimer.stop();
          tempTimer.action();
        }

        state.notificationTimer = new NotificationTimer(action, timeout + delay, type);
      } else {
        if (tempTimer) {
          tempTimer.stop();
        }

        state.notificationTimer = null;
      }
    },
    SET_SHOW_NOTIFICATION(state: any, show: any) {
      state.showNotification = show;
    },
  },
  actions: {
    notification_hover(context: any) {
      const timer = context.state.notificationTimer;
      if (timer) {
        timer.stop();
      }
    },
    notification_leave(context: any) {
      const timer = context.state.notificationTimer;
      if (timer) {
        timer.start();
      }
    },
    notification_close(context: any) {
      const timer = context.state.notificationTimer;
      if (timer) {
        timer.action();
      }
    },

    ok_click(context: any) {
      const tempTimer = context.state.notificationTimer;
      if (tempTimer) {
        tempTimer.stop();
        tempTimer.action();
      }
      context.commit('SET_SHOW_NOTIFICATION', false);
    },

    close_click(context: any) {
      context.commit('SET_NOTIFICATION_TIMER', null);
      context.commit('SET_SHOW_NOTIFICATION', false);
    },
    async copy_draft(context: any, url: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      sendGoogleEvent('copy_draft_link');

      const link = `${window.location.protocol}//${window.location.host}/${url}`;
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
      };
      await navigator.clipboard.writeText(link);

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 2000, action, type: 'copy_draft' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    delete_drafts(context: any, ids: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);

      async function _deleteDraft(ids: any) {
        await DraftsApi.delete(ids);
      }

      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
        await _deleteDraft(ids);
        sendGoogleEvent('delete_drafts');
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 4000, action, type: 'delete_drafts' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    delete_draft(context: any, id: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);

      async function _deleteDraft(id: any) {
        await DraftsApi.delete(id);
      }

      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
        await _deleteDraft(id);
        sendGoogleEvent('delete_draft');
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 4000, action, type: 'delete_draft' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    draft_comment_disabled(context: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 60000, action, type: 'draft_comment_disabled' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    maximum_characters(context: any) {
      if (!!context.state.notificationTimer && context.state.notificationTimer.type() === 'maximum_characters') {
        return;
      }

      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 4000, action, type: 'maximum_characters' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    delete_point(context: any, id: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
        await instance.delete(`/points/${id}`);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 4000, action, type: 'delete_point' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    delete_comment(context: any, id: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
        await instance.delete(`/comments/${id}`);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 4000, action, type: 'delete_comment' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },

    error_type(context: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 60000, action, type: 'error_type' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    fast_type(context: any, payload: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 0, action, type: 'fast_type' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
    any_type(context: any, payload: any) {
      context.commit('SET_SHOW_NOTIFICATION', false);
      const action = async () => {
        context.commit('SET_NOTIFICATION_TIMER', null);
        context.commit('SET_SHOW_NOTIFICATION', false);
      };

      context.commit('SET_NOTIFICATION_TIMER', { timeout: 4000, action, type: 'ok_notification' });
      setTimeout(() => context.commit('SET_SHOW_NOTIFICATION', true), 0);
    },
  },
});

export { notificationsStore };
